import type { CheckoutCarRentalServiceOption } from '@/scopes/checkout/domain/types'
import type {
  CarFilterOption,
  CarFilters,
  FilterableCarItem,
  MultiValueFilter,
  SingleValueFilter,
} from '@/scopes/checkout/filterableCars/domain/types'
import { defaultSelected } from '@/scopes/checkout/filterableCars/infrastructure/api/filter-mappers/singleValueFilterMapper'
import { isEmpty } from 'ramda'

export function selectedFiltersSelector(filters?: CarFilters): Record<string, string[]> | null {
  if (!filters || Object.keys(filters).length === 0)
    return null

  return Object.entries(filters).reduce<Record<string, string[]>>(
    (acc, [key, filter]: [string, SingleValueFilter & MultiValueFilter]) => {
      const selectedFilterName = filter.selectedFilter
        ? filter.filterOptions.find(
          (option: CarFilterOption) =>
            filter.selectedFilter !== defaultSelected && option.value === filter.selectedFilter,
        )?.name
        : null

      const selectedFiltersNames = filter.selectedFilters
        ? filter.filterOptions
            .filter((option: CarFilterOption) => filter.selectedFilters.includes(option.value))
            .map((option: CarFilterOption) => option.name)
        : []

      const allSelected = [...(selectedFilterName ? [selectedFilterName] : []), ...selectedFiltersNames]

      if (allSelected.length > 0) {
        acc[key] = allSelected
      }

      return acc
    },
    {},
  )
}

export function getTotalPriceForEquipmentsSelected(option: CheckoutCarRentalServiceOption) {
  const { equipments, selectedEquipmentsWithQuantity } = option

  if (!equipments?.length)
    return null
  if (!selectedEquipmentsWithQuantity?.length)
    return 0

  return selectedEquipmentsWithQuantity.reduce((acc, { id, quantity }) => {
    const equipment = equipments.find(({ id: equipmentId }) => equipmentId === id)
    if (!equipment)
      return acc

    const price = (equipment.price ?? 0) * quantity
    return acc + price
  }, 0)
}

export function isAnyCarOptionSelectedAndIncluded(carOptions: CheckoutCarRentalServiceOption[] | null, selectedCarIds?: string[]) {
  if (!carOptions)
    return false
  return carOptions.filter(({ id }) => selectedCarIds?.includes(id)).some(({ isIncluded }) => isIncluded)
}

export function isAnyCarOptionSelectedAndIncludedAndUpgradeable(carOptions: CheckoutCarRentalServiceOption[] | null, selectedCarIds?: string[]) {
  if (!carOptions)
    return false
  return carOptions.filter(({ id }) => selectedCarIds?.includes(id)).some(({ isIncluded, canBeUpgraded }) => isIncluded && canBeUpgraded)
}

export function getFirstCarIncluded(carOptions: CheckoutCarRentalServiceOption[] | null) {
  if (!carOptions)
    return null

  return carOptions.find(({ isIncluded }) => isIncluded) ?? null
}

export function getThePriceDifferenceToUpgradeIncludedCar(carOptions: FilterableCarItem[] | null, includedCar: CheckoutCarRentalServiceOption | null) {
  if (!carOptions)
    return null
  if (!includedCar)
    return null

  const upgradeableCars = carOptions.filter(({ price }) => price > includedCar.price)

  if (upgradeableCars.length === 0)
    return null

  const priceDifferences = upgradeableCars.map(({ price }) => price - includedCar.price)
  const minPriceDifference = Math.min(...priceDifferences)

  return minPriceDifference > 0 ? minPriceDifference : 0
}

export function getCarsByPage(cars: Record<string, FilterableCarItem> | undefined, carsPerPage: number, page: number) {
  if (!cars || isEmpty(cars))
    return {}
  const carsArray = Object.values(cars)
  const startIndex = (page - 1) * carsPerPage
  const endIndex = startIndex + carsPerPage
  const carsForPage = carsArray.slice(startIndex, endIndex)
  return carsForPage.reduce((acc, car) => {
    acc[car.id] = car
    return acc
  }, {} as Record<string, FilterableCarItem>)
}

export function getOptionEquipments(
  option: CheckoutCarRentalServiceOption | undefined,
  bookedEquipmentsWithQuantity:
    | {
      id: string
      quantity: number
    }[]
    | undefined,
) {
  if (!option)
    return []
  const { equipments, maxNumberOfEquipments } = option
  const equipmentsLimit = maxNumberOfEquipments ?? equipments.length
  const totalSelected = bookedEquipmentsWithQuantity?.reduce(
    (acc, item) => acc + item.quantity,
    0,
  ) ?? 0
  const limitReached = totalSelected >= equipmentsLimit

  return (
    equipments?.map((equipment) => {
      const bookedEquipment = bookedEquipmentsWithQuantity?.find(
        bookedEquipmentWithQuantity => bookedEquipmentWithQuantity.id === equipment.id,
      )
      const selected = Boolean(bookedEquipment)
      const quantitySelected = bookedEquipment?.quantity ?? 0

      const isElegible = selected || !limitReached

      return {
        ...equipment,
        selected,
        quantitySelected,
        isElegible,
      }
    }) ?? []
  )
}
